import React from 'react';
import { Mail, User, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export interface RoleUser {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'inactive';
}

export interface UsersTabProps<TUser extends RoleUser = RoleUser> {
  users: TUser[];
  roleName: string;
  onTransferUser: (user: TUser) => void;
}

export const UsersTab = <TUser extends RoleUser>({ users, roleName, onTransferUser }: UsersTabProps<TUser>) => (
  <>
    <div className="flex items-start gap-3 pb-4 border-b">
      <div className="rounded-lg bg-primary/10 p-2.5">
        <Users className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-semibold">Users Assigned to {roleName}</h3>
        <p className="text-sm text-muted-foreground mt-1">Manage users assigned to this role and transfer them to different roles</p>
      </div>
    </div>
    {users.length === 0 ? (
      <div className="flex items-center justify-center h-64 border rounded-lg bg-muted/20">
        <div className="text-center space-y-2">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No Users Assigned</p>
          <p className="text-sm text-muted-foreground">There are currently no users assigned to this role</p>
        </div>
      </div>
    ) : (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{users.length} {users.length === 1 ? 'user' : 'users'} assigned to this role</p>
        <div className="border rounded-lg divide-y">
          {users.map((user) => (
            <div key={user.id} className="p-4 hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>{user.status === 'active' ? 'Active' : 'Inactive'}</Badge>
                  <Button variant="outline" size="sm" onClick={() => onTransferUser(user)}>Transfer Role</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </>
);
